<script setup lang="ts">
import type { PropType } from 'vue'
import debounce from 'lodash/debounce.js'
import PresentationOverlay from '@/components/common/PresentationOverlay.vue'
import type { Category, Tag } from '@/models/content'
import IconLink from '@/assets/icons/light-link.svg'
import IconCheck from '@/assets/icons/icon-check.svg'

const { callToAction, isDownloading, downloadProgress } = useDownloadFile()

const { $auth, $cloudinary } = useNuxtApp()

const props = defineProps({
  id: {
    type: String,
    require: true,
  },
  linkFlipHtml5: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  coverUrl: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
  companyName: {
    type: String,
    require: true,
  },
  eventName: {
    type: String,
    require: true,
  },
  subscriptionRequired: {
    type: String,
    default: undefined,
  },
})

const router = useRouter()
const route = useRoute()
const relativeUrl = `${route.path}/${props.id}`
const url = `${window.location.href.split('?')[0]}/${props.id}`

const coverPosition = useCoverPosition(props.coverUrl)

function onClick() {
  if (callToAction.value.text === 'Login') {
    router.push(relativeUrl)
    return
  }
  callToAction.value.callback(
    props.id,
    'presentation',
    props.fileName,
    props.subscriptionRequired,
  )
}

const linkCopied = ref(false)
const debouncedCopyButton = debounce(() => {
  linkCopied.value = false
}, 2000)
const onShareClick = () => {
  navigator.clipboard.writeText(url)
  linkCopied.value = true
  debouncedCopyButton()
}
</script>

<template>
  <article
    class="card-hover group relative !cursor-default bg-s-200"
    data-testid="presentation-card"
  >
    <!-- Cover -->
    <section class="relative w-full grow">
      <!-- Image -->
      <div class="relative w-full pb-1/2">
        <img
          class="absolute left-0 top-0 h-full w-full object-cover"
          :class="coverPosition"
          :src="$cloudinary.resize(coverUrl, 'presentation-thumbnail')"
          alt=""
        />
        <div class="absolute h-full w-full border-2 border-black/10" />

        <PresentationOverlay
          :document-id="id"
          :link-flip-html5="linkFlipHtml5"
          :is-downloading="isDownloading"
          :download-progress="downloadProgress"
          :label="callToAction.text"
          :title="title"
          :subscription-required="subscriptionRequired"
          @on-download-click="onClick"
        />
      </div>

      <!-- Badges -->
      <div class="absolute left-0 top-0 flex w-full p-3">
        <div class="flex grow flex-wrap gap-2 pr-4">
          <LinkedPill v-for="pill in pills" :key="pill.id" :slug="pill.slug">
            {{ pill.name }}
          </LinkedPill>
        </div>
        <PresentationBadge />
      </div>
    </section>

    <!-- Info -->
    <section class="p-3">
      <h3 class="text-sm font-semibold" data-testid="presentation-card-title">
        {{ title }}
      </h3>
      <div class="mt-2">
        <p class="text-xs text-s-700" data-testid="presentation-card-company">
          <span class="font-bold">Company/Institution:</span>
          {{ companyName }}
        </p>
        <p class="text-xs text-s-700" data-testid="presentation-card-event">
          <span class="font-bold">Event:</span>
          {{ eventName }}
        </p>
        <p class="text-xs text-s-700" data-testid="presentation-card-date">
          <span class="font-bold">Date:</span>
          {{ publishedDate }}
        </p>
      </div>
    </section>
    <div
      v-if="$auth.store.isAuthenticated"
      class="absolute bottom-3 right-3 flex h-6 w-6 cursor-pointer items-center gap-x-1 p-0.5"
      @click="onShareClick"
    >
      <IconCheck
        class="absolute h-full w-full transition-opacity"
        :class="linkCopied ? 'opacity-100' : 'opacity-0'"
      />
      <IconLink
        class="absolute h-full w-full transition-opacity"
        :class="linkCopied ? 'opacity-0' : 'opacity-100'"
      />
    </div>
  </article>
</template>
